import { useRef, useState } from "react";
import api from "../lib/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Button } from "./ui/button";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SyncLibroMatricolaTab } from "./SyncLibroMatricolaTab";

export const ImportDialog = ({ open, onOpenChange, onImported }) => {
    const [activeTab, setActiveTab] = useState("import");
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setResult(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".xlsx")) {
            toast.error("Carica un file Excel (.xlsx)");
            return;
        }
        setFile(f);
        setResult(null);
    };

    const handleImport = async () => {
        if (!file) return;
        setImporting(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/employees/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            if (data.created > 0) {
                toast.success(`${data.created} dipendenti importati`);
                onImported && onImported();
            } else {
                toast.warning("Nessun nuovo dipendente importato");
            }
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Errore durante l'importazione";
            toast.error(msg);
            setResult({ error: msg });
        } finally {
            setImporting(false);
        }
    };

    const handleClose = (o) => {
        if (!o) reset();
        onOpenChange(o);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden" data-testid="import-dialog">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle className="font-display">Importa / Sincronizza Dipendenti</DialogTitle>
                    <DialogDescription>
                        Importa nuovi dipendenti da un file Excel, oppure sincronizza i dati esistenti col Libro Matricola.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex w-full min-h-0 flex-1 flex-col">
                    <TabsList className="mb-3 grid w-full flex-shrink-0 grid-cols-2">
                        <TabsTrigger value="import" data-testid="tab-import">
                            Importa nuovi
                        </TabsTrigger>
                        <TabsTrigger value="sync" data-testid="tab-sync">
                            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                            Sincronizza libro matricola
                        </TabsTrigger>
                    </TabsList>

                    {/* TAB 1: original "import new" flow */}
                    <TabsContent value="import" className="min-h-0 flex-1 overflow-y-auto pr-1">
                        <div className="space-y-3 text-xs text-slate-600">
                            Carica un file <code>.xlsx</code> con le seguenti colonne nella prima riga: <b>Cognome, Nome, Data Nascita, Qualifica, Data Assunzione, Matricola, Pos. INAIL, Pos. INPS</b>. Colonne opzionali: <b>Qualifica Evidenza</b> (sì/no), <b>Cliente Logo</b> (data URL base64). Le matricole già esistenti verranno <b>saltate</b>.
                        </div>

                        {!result && (
                            <div className="mt-4 space-y-4">
                                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                                    <FileSpreadsheet className="mx-auto h-10 w-10 text-slate-400" />
                                    <div className="mt-3 font-display text-sm font-semibold text-[#376DB3]">
                                        {file ? file.name : "Nessun file selezionato"}
                                    </div>
                                    {file && (
                                        <div className="mt-1 text-xs text-slate-500">
                                            {(file.size / 1024).toFixed(1)} KB
                                        </div>
                                    )}
                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept=".xlsx"
                                        className="hidden"
                                        onChange={handleFile}
                                        data-testid="import-file-input"
                                    />
                                    <Button
                                        variant="outline"
                                        className="mt-4 gap-2"
                                        onClick={() => inputRef.current?.click()}
                                        data-testid="import-file-pick"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {file ? "Cambia file" : "Seleziona file Excel"}
                                    </Button>
                                </div>

                                <div className="rounded-md bg-blue-50 px-4 py-3 text-xs text-blue-900">
                                    <div className="font-semibold">Formati data accettati</div>
                                    <div className="mt-1">YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY</div>
                                </div>
                            </div>
                        )}

                        {result && !result.error && (
                            <div className="mt-4 space-y-3" data-testid="import-result">
                                <div className="flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                    <div>
                                        <div className="text-sm font-semibold text-emerald-900">
                                            {result.created} dipendenti importati
                                        </div>
                                        {result.skipped > 0 && (
                                            <div className="text-xs text-emerald-700">
                                                {result.skipped} saltati (matricola già esistente)
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {result.errors && result.errors.length > 0 && (
                                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                                            <AlertTriangle className="h-4 w-4" /> Errori ({result.errors.length})
                                        </div>
                                        <div className="mt-2 max-h-48 overflow-y-auto text-xs text-amber-800">
                                            {result.errors.map((err, i) => (
                                                <div key={i} className="border-b border-amber-200 py-1 last:border-b-0">
                                                    <span className="font-mono-numbers font-semibold">Riga {err.row}:</span> {err.error}
                                                    {err.matricola && (
                                                        <span className="text-amber-600"> ({err.matricola})</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {result && result.error && (
                            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                {result.error}
                            </div>
                        )}
                    </TabsContent>

                    {/* TAB 2: sync libro matricola */}
                    <TabsContent value="sync" className="min-h-0 flex-1 overflow-y-auto pr-1">
                        <SyncLibroMatricolaTab onSynced={onImported} />
                    </TabsContent>
                </Tabs>

                <DialogFooter className="flex-shrink-0 border-t border-slate-100 pt-3">
                    {activeTab === "import" && result ? (
                        <>
                            <Button variant="outline" onClick={reset} data-testid="import-new">
                                Nuova importazione
                            </Button>
                            <Button
                                onClick={() => handleClose(false)}
                                className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="import-close"
                            >
                                Chiudi
                            </Button>
                        </>
                    ) : activeTab === "import" ? (
                        <>
                            <Button variant="outline" onClick={() => handleClose(false)} data-testid="import-cancel">
                                Annulla
                            </Button>
                            <Button
                                onClick={handleImport}
                                disabled={!file || importing}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="import-submit"
                            >
                                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                Importa
                            </Button>
                        </>
                    ) : (
                        <Button variant="outline" onClick={() => handleClose(false)} data-testid="sync-close">
                            Chiudi
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
